"use client";

import { useRouter } from "next/navigation";
import { useCallback } from "react";
import { ApiError } from "@/lib/api";

/**
 * EPIC-050 STORY-2201/2200 — `<KycGate>` redirect helper.
 *
 * The backend's `RequireKycTierGuard` (apps/api/src/auth/
 * require-kyc-tier.guard.ts) throws structured 403s on tier
 * shortfall:
 *
 *   {
 *     error: 'Tier1KycRequired' | 'Tier2KycRequired' | 'Tier3KycRequired',
 *     requiredTier: number,
 *     currentTier: number,
 *     redirectPath: '/kyc/tier1' | '/kyc/tier2' | '/kyc/tier3',
 *     message: <user-facing fallback>,
 *   }
 *
 * The frontend's `apiFetch` parses this into an `ApiError` with
 * `code = 'Tier{N}KycRequired'` (the response body's `error`
 * field) + `rawMessage = <message>`. This hook returns a callback
 * that consumers invoke in their `catch` blocks:
 *
 *   const kycRedirect = useKycGateRedirect();
 *   try { await api.placeOrder(...); }
 *   catch (err) {
 *     if (kycRedirect(err)) return; // bail — redirect in flight
 *     // ...other error handling
 *   }
 *
 * The callback returns `true` when it consumed the error (i.e.
 * redirect dispatched) so the caller can short-circuit any other
 * error UI it would otherwise render. Returns `false` for any
 * non-Tier error so the caller's regular error path runs.
 *
 * Why a hook instead of a wrapper component:
 *   - Most KYC-gated calls happen inside event handlers (button
 *     clicks, form submits), not at component-mount time. A
 *     wrapper around the surface can't see that the API call
 *     failed mid-handler.
 *   - The hook composes with any existing try/catch + `setError`
 *     state machine — no rewrite of the consumer.
 *   - Adding a separate `<KycGate>` wrapper later (for surfaces
 *     that want to *preempt* the click — e.g. show a banner
 *     before the user even tries to deposit) is additive: the
 *     wrapper can call `api.fetchSelf()` on mount + use the
 *     same redirect logic if the user is below tier.
 *
 * The redirect path includes `?next=` so the /kyc/tier{N} page
 * can route back to the original surface after promotion. The
 * page's auth-guard already handles the `next=` round-trip.
 */
export function useKycGateRedirect() {
  const router = useRouter();
  return useCallback(
    (err: unknown): boolean => {
      if (!(err instanceof ApiError)) return false;
      // Match Tier{1|2|3}KycRequired — case-insensitive because the
      // AllExceptionsFilter uppercases error codes ("TIER1KYCREQUIRED"),
      // while the hook's spec mocks use the original camel-case. The
      // /i flag bridges both shapes so the gate fires correctly on
      // real 403 responses.
      const match = /^Tier([123])KycRequired$/i.exec(err.code);
      if (!match) return false;
      const tier = Number(match[1]);
      // Tier 1 is the light-KYC modal flow — fire a window event the
      // <Kyc1AutoModal> listens for and renders inline. Avoids ripping
      // the user out of context with a full page route to /kyc/tier1.
      // Tier 2/3 still page-route (gov-ID + source-of-funds flows
      // need the full Sumsub embed surface, not a 3-step modal).
      if (tier === 1 && typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("granbay.kyc1.required"));
        return true;
      }
      const here =
        typeof window !== "undefined"
          ? window.location.pathname + window.location.search
          : "/home";
      router.push(`/kyc/tier${tier}?next=${encodeURIComponent(here)}`);
      return true;
    },
    [router],
  );
}
